import React, { useRef, useState } from 'react';
import { router } from '@inertiajs/react';
import AiHandoffSheet from '../components/Todo/AiHandoffSheet';
import Sidebar from '../components/Todo/Sidebar';
import TodoLayout from '../layouts/TodoLayout';
import useTaskSearch from '../hooks/useTaskSearch';
import api from '../lib/api';
import type { HandoffTarget } from '../types/aiHandoff';
import type { Project } from '../types/project';
import type { TodoCounts, TodoFacts, TodoPayload } from '../types/todo';

// today 不是顶层 prop —— 它在 TodoPayload 里（TodoQuery 塞的 today: Date.current）。
// Todo.tsx 传给 Sidebar 的也是 todo.today。写成顶层 today 的话 TypeScript 不会
// 报错（Inertia 的 props 运行时无类型），页面上会直接显示「NaN月NaN日 undefined」。
type TodoTrashProps = {
  todo: TodoPayload;
  counts: TodoCounts;
  projects: Project[];
  facts: TodoFacts;
};

type TodoTrashPage = React.FC<TodoTrashProps> & {
  layout?: (page: React.ReactNode) => React.ReactNode;
};

// 回收站。刻意只有列表 + 恢复按钮：不搜回收站自己的内容、没有分页、没有
// 批量 —— 里面最多躺 30 天的量，单人应用规模下这些都是提前优化。
//
// 左栏那个搜索框是另一回事：它搜的是任务，不是回收站。在这一页敲字会把你
// 带到搜索结果那一栏 —— 与下面那条「同一个边栏不该在不同页面长出不同的
// 能力」是同一个理由。
//
// 列表里只有「删除起点」（TodoQuery#trash_scope 已经过滤过）：跟着父任务
// 一起删的子任务不单独出现，因为恢复父任务就会带回它们，单独列出来只会
// 让人以为可以单独恢复。
const TodoTrash: TodoTrashPage = ({ todo, counts, projects, facts }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // 左栏在回收站页也是同一个左栏，清单级入口跟着一起有 —— 同一个边栏
  // 在不同页面长出不同的能力，那是最难解释的一种不一致。
  const [handoff, setHandoff] = useState<HandoffTarget | null>(null);
  const [restoringId, setRestoringId] = useState<number | null>(null);
  // 回收站页永远不是搜索态（搜索结果落在待办池那一栏），所以 activeQuery
  // 恒为 undefined；清掉就留在这一页。
  const search = useTaskSearch({ activeQuery: undefined, originHref: '/todo/trash' });
  const searchRef = useRef<HTMLInputElement | null>(null);

  // 走 api 客户端而不是 router.post：Web::Internal 回的是纯 JSON，用 Inertia
  // 访问打过去会直接抛「All Inertia requests must receive a valid Inertia
  // response」并把整页糊上错误框。这一层是背景 XHR，重载页面才是 Inertia 的活。
  const restore = async (id: number) => {
    setRestoringId(id);
    try {
      await api.tasks.restore(id);
      // 恢复完停在回收站页，只是那一条不见了 —— 连着恢复几条时不必来回跳。
      // reload 默认就保留滚动位置，不必（也不能）传 preserveScroll。
      router.reload();
    } finally {
      setRestoringId(null);
    }
  };

  // 与 Todo.tsx 同一套网格，只是没有第三栏：侧栏的宽度靠这里的列宽给（它自己
  // 在 sm: 之后是 w-auto）。写成 flex 的话它会退化成内容宽度，同一个侧栏在
  // /todo 是 252px、在这一页是 177px。
  return (
    <div className="grid h-full grid-cols-[minmax(0,1fr)] bg-todo-list sm:grid-cols-[220px_minmax(0,1fr)] wide:grid-cols-[252px_minmax(0,1fr)]">
      <Sidebar
        view="trash"
        projectId={null}
        counts={counts}
        projects={projects}
        facts={facts}
        today={todo.today}
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        onHandoffList={(v, pid, name, count) =>
          setHandoff({ kind: 'list', view: v, projectId: pid, name, count })
        }
        searchBox={{
          draft: search.draft,
          inputRef: searchRef,
          onChange: search.setDraft,
          onClear: search.clear,
        }}
      />

      <AiHandoffSheet target={handoff} onClose={() => setHandoff(null)} />

      {/* 标题块与列表区的分法照 TaskListPane：内边距、字号、副标题的位置都取它
          的值，切视图时标题不会跳一下。列表单独一个可滚区域，标题跟着固定。 */}
      <div className="flex min-h-0 min-w-0 flex-col">
        <div className="px-[22px] pb-[10px] pt-[18px]">
          {/* 窄屏下侧栏是抽屉，得有个东西能把它叫出来。TaskListPane 那边是
              「标题 ▾」，这一页只有一个视图，没有可切换的语义，所以是菜单按钮。 */}
          <button
            type="button"
            className="-ml-1 mb-1 px-1 text-[13.5px] text-todo-ink2 sm:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            ☰ 菜单
          </button>

          <h1 className="text-[21px] font-semibold tracking-tight">回收站</h1>
          <div className="mt-[3px] text-[12px] text-todo-muted">
            删除的任务在这里保留 30 天，之后自动清除。
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-[22px] pb-4">
          {todo.tasks.length === 0 ? (
            <p className="text-[13.5px] text-todo-muted">回收站是空的。</p>
          ) : (
            <ul className="divide-y divide-todo-line">
              {todo.tasks.map((task) => (
                <li key={task.id} className="flex items-center gap-3 py-3">
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-[13.5px] text-todo-ink">{task.title}</div>
                    {task.project && (
                      <div className="mt-0.5 text-[12px] text-todo-muted">{task.project.name}</div>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => restore(task.id)}
                    disabled={restoringId === task.id}
                    className="shrink-0 rounded-[7px] border border-todo-line px-3 py-1
                               text-[12.5px] text-todo-ink2 hover:bg-todo-card2
                               disabled:opacity-50"
                  >
                    {restoringId === task.id ? '恢复中…' : '恢复'}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

// 必须显式挂 TodoLayout。不写的话 entrypoints/inertia.tsx 那条兜底
// （`page.default.layout ??= AppLayout`）会静默生效，于是这一页跑在
// `container mx-auto p-4` 里：侧栏被挤窄、h-full 没有高度基准、底下多一个
// Footer、顶上多一条与 /todo 中栏重复的 TimerBar。不报错，只是长得不对。
TodoTrash.layout = (page: React.ReactNode) => <TodoLayout>{page}</TodoLayout>;

export default TodoTrash;
